import { ProfilePhoto } from "@/containers/Profile/components";
import Rights from "@/enums/Rights";
import { mergeUserContexts } from "@/helpers";
import { IUserSearchInfo } from "@/interfaces/User/IUserSearchInfo";
import { useUserContext } from "@/providers";
import { useToggleHideViperUserMutation } from "@/services";
import { Button, Card, Checkbox, Descriptions, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

interface IProps {
  user: IUserSearchInfo;
  refetchUser?: () => void;
}

export const UserInfoCard = ({ user, refetchUser }: IProps) => {
  const { userToken, hasRight, setImpersonationUserId, isUserTokenLoading } =
    useUserContext();
  const intl = useIntl();

  const [hideViperUser, setHideViperUser] = useState(false);

  const [userAccess, setUserAccess] = useState({
    canImpersonate: false,
    canViewPermissions: false,
    canWritePermissions: false,
  });

  const [toggleHideViperUser] = useToggleHideViperUserMutation();
  const viewMode = !refetchUser;

  useEffect(() => {
    setUserAccess({
      canImpersonate: hasRight([Rights.impersonateUsers]),
      canViewPermissions: hasRight([
        Rights.readPermissions,
        Rights.writePermissions,
      ]),
      canWritePermissions: hasRight([Rights.writePermissions]),
    });
  }, [hasRight]);

  useEffect(() => {
    if (!user) return;
    const userContext = mergeUserContexts(user);
    setHideViperUser((userContext?.roles || []).includes(Rights.hideViperUser));
  }, [user, userToken, userToken.user.id]);

  const handleImpersonateClick = () => {
    setImpersonationUserId(user?.id);
  };

  const handleHideUser = () => {
    toggleHideViperUser(user?.id)
      .unwrap()
      .then(() => {
        toast.success(intl.formatMessage({ id: "profile.toggleUserSuccess" }));
        if (refetchUser) refetchUser();
      })
      .catch(() => {
        toast.error(intl.formatMessage({ id: "general.error" }));
      });
  };

  // Display user details once we have user information
  const userDetails = user && (
    <Descriptions
      title={
        <>
          <Typography.Title level={3}>
            {user.displayName}
            {userAccess.canImpersonate && (
              <Button
                className="ml-2"
                data-test-id="profile-impersonate-button"
                loading={isUserTokenLoading}
                onClick={handleImpersonateClick}
              >
                {intl.formatMessage({ id: "profile.impersonateButton" })}
              </Button>
            )}
          </Typography.Title>
          {userAccess.canWritePermissions && (
            <Checkbox
              data-test-id="profile-hideuser-checkbox"
              onChange={handleHideUser}
              checked={hideViperUser}
              disabled={viewMode}
            >
              {intl.formatMessage({ id: "profile.hideUserCheckbox" })}
            </Checkbox>
          )}
        </>
      }
      size="small"
      bordered
      column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
    >
      <Descriptions.Item
        label={intl.formatMessage({ id: "profile.jobTitleLabel" })}
      >
        {user.jobTitle}
      </Descriptions.Item>
      <Descriptions.Item
        label={intl.formatMessage({ id: "profile.departmentLabel" })}
      >
        {user.department}
      </Descriptions.Item>
      <Descriptions.Item
        label={intl.formatMessage({ id: "profile.locationLabel" })}
      >
        {user.officeLocation}
      </Descriptions.Item>
      <Descriptions.Item
        label={intl.formatMessage({ id: "profile.managerLabel" })}
      >
        {user.manager && user.manager.displayName}
      </Descriptions.Item>
      <Descriptions.Item
        label={intl.formatMessage({ id: "profile.phoneLabel" })}
      >
        {user.mobilePhone}
      </Descriptions.Item>
      <Descriptions.Item
        label={intl.formatMessage({ id: "profile.emailLabel" })}
      >
        {user.email}
      </Descriptions.Item>
    </Descriptions>
  );

  if (!user) return null;

  return (
    <Card>
      <Space direction="horizontal" size="large">
        {user.id && <ProfilePhoto userId={user.id} />}
        {userDetails}
      </Space>
    </Card>
  );
};
